// Units (route /more/units): five choices, applied globally the moment they
// save. Storage everywhere stays canonical (shared/units.ts) — these switches
// only change how numbers are shown and typed.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { useMe, invalidateMe } from "../lib/useMe";
import { DEFAULT_UNITS, type UnitPrefs } from "@shared/units";
import { SectionHeader } from "../components/ui/SectionHeader";

const LABELS: Record<keyof UnitPrefs, Array<{ v: string; key: string }>> = {
  weight: [{ v: "kg", key: "units.kg" }, { v: "lb", key: "units.lb" }],
  length: [{ v: "cm", key: "units.cm" }, { v: "in", key: "units.in" }],
  energy: [{ v: "kcal", key: "units.kcal" }, { v: "kj", key: "units.kj" }],
  serving: [{ v: "metric", key: "units.metric" }, { v: "imperial", key: "units.imperial" }],
  glucose: [{ v: "mgdl", key: "units.mgdl" }, { v: "mmol", key: "units.mmol" }],
};
const ORDER: Array<{ k: keyof UnitPrefs; titleKey: string; hintKey?: string }> = [
  { k: "weight", titleKey: "units.weight" },
  { k: "length", titleKey: "units.length" },
  { k: "energy", titleKey: "units.energy" },
  { k: "serving", titleKey: "units.serving", hintKey: "units.serving_hint" },
  { k: "glucose", titleKey: "units.glucose", hintKey: "units.glucose_hint" },
];

export function UnitsSettings() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useMe();
  const [prefs, setPrefs] = useState<UnitPrefs>(DEFAULT_UNITS);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (me?.unit_prefs) setPrefs(me.unit_prefs); }, [me]);

  async function pick<K extends keyof UnitPrefs>(k: K, v: UnitPrefs[K]) {
    if (saving || prefs[k] === v) return;
    const next = { ...prefs, [k]: v };
    const prev = prefs;
    setPrefs(next);       // optimistic — the segmented control answers the tap
    setSaving(true);
    try {
      await api.putProfile({ unit_prefs: next });
      invalidateMe();     // every screen re-reads and re-renders in the new unit
    } catch {
      setPrefs(prev);
      toast.error(t("common.failed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5 pt-4 animate-rise pb-8">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} aria-label={t("common.back")} className="text-fg-muted text-2xl leading-none -ml-1 press min-w-[44px] min-h-[44px] text-left">‹</button>
        <h1 className="text-title font-bold tracking-tight">{t("units.title")}</h1>
      </div>
      <p className="text-sm text-fg-muted leading-relaxed">{t("units.intro")}</p>

      {ORDER.map(({ k, titleKey, hintKey }) => (
        <section key={k} className="space-y-2">
          <SectionHeader>{t(titleKey)}</SectionHeader>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t(titleKey)}>
            {LABELS[k].map(({ v, key }) => {
              const active = prefs[k] === v;
              return (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => pick(k, v as UnitPrefs[typeof k])}
                  className={`py-3 px-3 rounded-xl text-sm font-medium transition press ${
                    active ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary"
                  }`}
                >
                  {t(key)}
                </button>
              );
            })}
          </div>
          {hintKey && <p className="text-sm text-fg-faint leading-relaxed">{t(hintKey)}</p>}
        </section>
      ))}
    </div>
  );
}
